"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const ContentAligner_1 = require("./ContentAligner");
const LineLayouter_1 = require("./LineLayouter");
const ItemCoordinatesUpdater_1 = require("./ItemCoordinatesUpdater");
/**
 * Layouts a flex container (and descendants).
 */
class FlexLayouter {
    constructor(container) {
        this.container = container;
        this.lineLayouter = new LineLayouter_1.LineLayouter(this);
        this.resizingMainAxis = false;
        this.resizingCrossAxis = false;
        this.cachedInitialWidth = 0;
        this.cachedInitialHeight = 0;
        this.cachedLayoutWidth = 0;
        this.cachedLayoutHeight = 0;
        this.shrunk = false;
        this.totalCrossAxisSize = 0;
    }
    layoutTree() {
        if (this.isSubTree()) {
            // This can occur if only a part of the flex hierarchy needs to be updated.
            if (!this.shouldSkipSubTree()) {
                this.updateSubTreeLayout();
                this.updateItemCoords();
            }
        }
        else {
            this.updateTreeLayout();
            this.updateItemCoords();
        }
    }
    isSubTree() {
        return !!this.item.flexParent;
    }
    shouldSkipSubTree() {
        // If the recalc flag has already been cleared when getting here, we can assume that this subtree was already
        // updated as part of a parent flex tree update. In that case we must not update this subtree individually as it
        // may fail to take the flex ancestors into account - giving incorrect results.
        return !this.container.node.isChanged();
    }
    updateTreeLayout() {
        if (this.recalc) {
            this.performUpdateLayoutTree();
        }
        else {
            this.performUpdateLayoutTreeFromCache();
        }
    }
    performUpdateLayoutTree() {
        this.setCachedInitialSizes();
        this.initializeAxisSizes();
        this.layoutAxes();
        this.setLayoutCache();
    }
    setCachedInitialSizes() {
        this.cachedInitialWidth = this.item.getRelAxisSize(true);
        this.cachedInitialHeight = this.item.getRelAxisSize(false);
    }
    setLayoutCache() {
        this.cachedLayoutWidth = this.item.getAxisLayoutSize(true);
        this.cachedLayoutHeight = this.item.getAxisLayoutSize(false);
    }
    performUpdateLayoutTreeFromCache() {
        if (this.isInitialSizeCacheValid()) {
            this.useLayoutCache();
        }
        else {
            this.item.enableLocalRecalcFlag();
            this.performUpdateLayoutTree();
        }
    }
    useLayoutCache() {
        this.item.setAxisLayoutSize(true, this.cachedLayoutWidth);
        this.item.setAxisLayoutSize(false, this.cachedLayoutHeight);
    }
    isInitialSizeCacheValid() {
        const initialWidth = this.item.getRelAxisSize(true);
        const initialHeight = this.item.getRelAxisSize(false);
        return this.cachedInitialWidth === initialWidth && this.cachedInitialHeight === initialHeight;
    }
    updateItemCoords() {
        const updater = new ItemCoordinatesUpdater_1.ItemCoordinatesUpdater(this);
        updater.finalize();
    }
    updateSubTreeLayout() {
        // The dimensions of this container are guaranteed not to have changed.
        // That's why we can safely 'reuse' those and re-layout the contents.
        const crossAxisSize = this.crossAxisSize;
        this.layoutMainAxis();
        this.performResizeCrossAxis(crossAxisSize);
    }
    initializeAxisSizes() {
        if (this.item.isFlexItemEnabled()) {
            if (this.item.flexItem) {
                this.item.flexItem.resetLayoutSize();
            }
        }
        else {
            this.mainAxisSize = this.getMainAxisBasis();
            this.crossAxisSize = this.getCrossAxisBasis();
        }
        this.resizingMainAxis = false;
        this.resizingCrossAxis = false;
        this.shrunk = false;
    }
    layoutAxes() {
        this.layoutMainAxis();
        this.layoutCrossAxis();
    }
    layoutMainAxis() {
        this.lineLayouter.layoutLines();
        this.fitMainAxisSizeToContents();
    }
    getLines() {
        return this.lineLayouter.getLines();
    }
    fitMainAxisSizeToContents() {
        if (!this.resizingMainAxis) {
            if (this.isMainAxisFitToContents()) {
                this.mainAxisSize = this.lineLayouter.getMainAxisContentSize();
            }
        }
    }
    layoutCrossAxis() {
        const aligner = new ContentAligner_1.ContentAligner(this);
        this.totalCrossAxisSize = aligner.getTotalCrossAxisSize();
        this.fitCrossAxisSizeToContents();
        aligner.align();
    }
    fitCrossAxisSizeToContents() {
        if (!this.resizingCrossAxis) {
            if (this.isCrossAxisFitToContents()) {
                this.crossAxisSize = this.totalCrossAxisSize;
            }
        }
    }
    isWrapping() {
        return this.container.wrap;
    }
    isAxisFitToContents(horizontal) {
        if (this.horizontal === horizontal) {
            return this.isMainAxisFitToContents();
        }
        else {
            return this.isCrossAxisFitToContents();
        }
    }
    isMainAxisFitToContents() {
        return !this.isWrapping() && !this.hasFixedMainAxisBasis();
    }
    isCrossAxisFitToContents() {
        return !this.hasFixedCrossAxisBasis();
    }
    hasFixedMainAxisBasis() {
        return !this.item.isZeroAxisSize(this.horizontal);
    }
    hasFixedCrossAxisBasis() {
        return !this.item.isZeroAxisSize(!this.horizontal);
    }
    getAxisMinLineSize(horizontal) {
        if (this.horizontal === horizontal) {
            return this.lineLayouter.getMainAxisMinSize();
        }
        else {
            return this.lineLayouter.getCrossAxisMinSize();
        }
    }
    resizeMainAxis(size) {
        if (this.mainAxisSize !== size) {
            if (this.recalc > 0) {
                this.performResizeMainAxis(size);
            }
            else {
                if (this.checkValidCacheMainAxisResize(size)) {
                    this.mainAxisSize = size;
                    this.fitCrossAxisSizeToContents();
                }
                else {
                    // Cache miss.
                    this.item.enableLocalRecalcFlag();
                    this.performResizeMainAxis(size);
                }
            }
        }
    }
    checkValidCacheMainAxisResize(size) {
        const isFinalMainAxisSize = size === this.subjectMainAxisSize;
        if (isFinalMainAxisSize) {
            return true;
        }
        const canIgnoreCacheMiss = !this.isCrossAxisFitToContents();
        // Allow other main axis resizes and check if final resize matches the subject main axis size
        //  (ItemCoordinatesUpdater).
        return canIgnoreCacheMiss;
    }
    performResizeMainAxis(size) {
        this.shrunk = size < this.mainAxisSize;
        this.mainAxisSize = size;
        this.resizingMainAxis = true;
        this.layoutAxes();
        this.resizingMainAxis = false;
    }
    resizeCrossAxis(size) {
        if (this.crossAxisSize !== size) {
            if (this.recalc > 0) {
                this.performResizeCrossAxis(size);
            }
            else {
                this.crossAxisSize = size;
            }
        }
    }
    performResizeCrossAxis(size) {
        this.crossAxisSize = size;
        this.resizingCrossAxis = true;
        this.layoutCrossAxis();
        this.resizingCrossAxis = false;
    }
    get subjectMainAxisSize() {
        return this.horizontal ? this.item.subject.getLayoutW() : this.item.subject.getLayoutH();
    }
    getParentFlexContainer() {
        return this.item.isFlexItemEnabled() ? this.item.flexItem.getContainer() : undefined;
    }
    getMainAxisBasis() {
        return this.item.getRelAxisSize(this.horizontal);
    }
    getCrossAxisBasis() {
        return this.item.getRelAxisSize(!this.horizontal);
    }
    get horizontal() {
        return this.container.horizontal;
    }
    get reverse() {
        return this.container.reverse;
    }
    get item() {
        return this.container.node;
    }
    get items() {
        return this.item.items;
    }
    isResizingMainAxis() {
        return this.resizingMainAxis;
    }
    isResizingCrossAxis() {
        return this.resizingCrossAxis;
    }
    get mainAxisSize() {
        return this.item.getAxisLayoutSize(this.horizontal);
    }
    set mainAxisSize(v) {
        this.item.setAxisLayoutSize(this.horizontal, v);
    }
    get crossAxisSize() {
        return this.item.getAxisLayoutSize(!this.horizontal);
    }
    set crossAxisSize(v) {
        this.item.setAxisLayoutSize(!this.horizontal, v);
    }
    hasShrunk() {
        return this.shrunk;
    }
    get recalc() {
        return this.item.recalc;
    }
}
exports.FlexLayouter = FlexLayouter;
//# sourceMappingURL=FlexLayouter.js.map